/**
 * Multi-Entity Selector
 * Allows selecting multiple entities (locations, vendors, etc.) for comparison
 * Uses Popover UI matching the LocationSelector / ENG-2279 checkbox-menu design
 */
import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Text,
    HStack,
} from '@chakra-ui/react';
import { DomainStatItem, DomainType } from '../../../types/reporting';
import { MULTI_ENTITY_COLORS } from '../shared/formatters';
import {
    CheckboxMenuSearch,
    CheckboxMenuRow,
    CheckboxMenuList,
    CheckboxMenuFooter,
    CheckboxMenuTrigger,
    CheckboxMenuPinnedAll,
    TokenChip,
    checkboxMenuPopoverContentSx,
} from '../../../ui/primitives/CheckboxMenu';

interface EntitySelectorLabels {
    all: string;
    singular: string;
    plural: string;
}

interface MultiEntitySelectorProps {
    domain: DomainType;
    entities: DomainStatItem[];
    selectedIds: string[];
    onChange: (entityIds: string[]) => void;
    maxSelections?: number;
    width?: string;
    usePortal?: boolean;
    /** Override default domain labels (e.g. vendor preference tiers vs vendors). */
    labels?: EntitySelectorLabels;
}

const DOMAIN_LABELS: Record<DomainType, { all: string; singular: string; plural: string }> = {
    location: { all: 'All Locations', singular: 'Location', plural: 'Locations' },
    vendor: { all: 'All Vendors', singular: 'Vendor', plural: 'Vendors' },
    insurance: { all: 'All Insurance', singular: 'Carrier', plural: 'Carriers' },
    oem: { all: 'All OEMs', singular: 'OEM', plural: 'OEMs' },
};

// Colors for multi-entity comparison (up to 5 entities). Shared with the donut palette.
export const ENTITY_COLORS = MULTI_ENTITY_COLORS;

const MultiEntitySelector = ({
    domain,
    entities,
    selectedIds,
    onChange,
    maxSelections = 5,
    width = '180px',
    usePortal: shouldUsePortal = false,
    labels: labelsOverride,
}: MultiEntitySelectorProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [popoverWidth, setPopoverWidth] = useState<number | string>(width);

    const labels = labelsOverride ?? DOMAIN_LABELS[domain];

    useEffect(() => {
        if (isPopoverOpen && triggerRef.current) {
            setPopoverWidth(Math.max(triggerRef.current.offsetWidth, 260));
        }
    }, [isPopoverOpen]);

    useEffect(() => {
        if (!isPopoverOpen) {
            setSearchTerm('');
        }
    }, [isPopoverOpen]);

    const filteredEntities = entities.filter(e =>
        searchTerm === '' || (e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
    );

    const selectedNames = selectedIds
        .map(id => entities.find(e => e.id === id)?.name)
        .filter(Boolean) as string[];

    const handleToggle = (id: string) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(v => v !== id));
            return;
        }
        if (selectedIds.length < maxSelections) {
            onChange([...selectedIds, id]);
        }
    };

    return (
        <Popover
            placement="bottom-end"
            isOpen={isPopoverOpen}
            onOpen={() => setIsPopoverOpen(true)}
            onClose={() => setIsPopoverOpen(false)}
            gutter={8}
        >
            <PopoverTrigger>
                <Box w={width} ref={triggerRef}>
                    <CheckboxMenuTrigger
                        isOpen={isPopoverOpen}
                        tokens={selectedNames}
                        emptyLabel={labels.all}
                    />
                </Box>
            </PopoverTrigger>
            {(() => {
                const popoverContent = (
                    <PopoverContent
                        w={typeof popoverWidth === 'number' ? `${popoverWidth}px` : popoverWidth}
                        zIndex={2000}
                        sx={checkboxMenuPopoverContentSx}
                    >
                        <PopoverBody p={0}>
                            <CheckboxMenuSearch
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder={`Search ${labels.plural.toLowerCase()}...`}
                            />
                            {selectedIds.length > 0 && (
                                <HStack px="10px" pt="8px" pb="2px" spacing="5px" flexWrap="wrap">
                                    {selectedIds.map((id, index) => {
                                        const entity = entities.find(e => e.id === id);
                                        if (!entity) return null;
                                        const color = ENTITY_COLORS[index % ENTITY_COLORS.length];
                                        return (
                                            <TokenChip key={id}>
                                                <Box as="span" color={color}>{entity.name}</Box>
                                            </TokenChip>
                                        );
                                    })}
                                </HStack>
                            )}
                            <CheckboxMenuPinnedAll
                                isSelected={selectedIds.length === 0}
                                onClick={() => onChange([])}
                            >
                                {labels.all}
                            </CheckboxMenuPinnedAll>
                            <CheckboxMenuList>
                                {filteredEntities.map((entity) => {
                                    const isSelected = selectedIds.includes(entity.id);
                                    const isDisabled = !isSelected && selectedIds.length >= maxSelections;
                                    return (
                                        <CheckboxMenuRow
                                            key={entity.id}
                                            isSelected={isSelected}
                                            isDisabled={isDisabled}
                                            onClick={() => handleToggle(entity.id)}
                                        >
                                            {entity.name}
                                        </CheckboxMenuRow>
                                    );
                                })}
                                {filteredEntities.length === 0 && (
                                    <Text fontSize="13px" color="#718096" px="10px" py="8px">
                                        No matches found
                                    </Text>
                                )}
                            </CheckboxMenuList>
                            <CheckboxMenuFooter
                                selectedCount={selectedIds.length}
                                totalCount={entities.length}
                                onClear={() => onChange([])}
                            />
                        </PopoverBody>
                    </PopoverContent>
                );
                return shouldUsePortal ? <Portal>{popoverContent}</Portal> : popoverContent;
            })()}
        </Popover>
    );
};

export default MultiEntitySelector;
